import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  Download,
  Upload,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  FileSpreadsheet,
  Users,
  Clock,
  XCircle,
} from "lucide-react";


export default function HrUpdatePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [linkInfo, setLinkInfo] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    checkLinkValidity();
  }, [token]);

  const checkLinkValidity = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/hr-update/${token}/info`);
      setLinkInfo(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("This link is invalid or has been revoked.");
      } else if (err.response?.status === 410) {
        setError("This link has expired.");
      } else {
        setError("Unable to verify link. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/public/hr-update/${token}/download`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'Staff_Directory_HR_Update.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Staff directory downloaded!");
    } catch (err) {
      toast.error("Failed to download file");
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/public/hr-update/${token}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadResult(response.data);
      toast.success(response.data.message || "Upload successful!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      setUploadResult({ success: false, error: err.response?.data?.detail });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <RefreshCw className="h-6 w-6 animate-spin" />
          <span>Verifying link...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Link Not Valid</h2>
            <p className="text-slate-600">{error}</p>
            <p className="text-sm text-slate-500 mt-4">
              Please contact your administrator to get a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (linkInfo?.expired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Clock className="h-16 w-16 mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Link Expired</h2>
            <p className="text-slate-600">
              This HR update link has expired on {new Date(linkInfo.expires_at).toLocaleDateString()}.
            </p>
            <p className="text-sm text-slate-500 mt-4">
              Please contact your administrator to get a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 p-4">
      <div className="max-w-2xl mx-auto pt-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Users className="h-10 w-10 text-indigo-600" />
            <h1 className="text-3xl font-bold text-slate-800">Staff Directory Update</h1>
          </div>
          <p className="text-slate-600">
            Download the current staff list, make your updates in Excel, then upload the updated file.
          </p>
          {linkInfo?.created_by && (
            <p className="text-sm text-slate-500 mt-2">
              Link created by: {linkInfo.created_by}
            </p>
          )}
        </div>

        {/* Step 1: Download */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold">1</span>
              Download Current Staff List
            </CardTitle>
            <CardDescription>
              Download the Excel file with all current staff records
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleDownload}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="hr-download-btn"
            >
              <Download className="h-5 w-5 mr-2" />
              Download Staff Directory (.xlsx)
            </Button>
          </CardContent>
        </Card>

        {/* Step 2: Edit Instructions */}
        <Card className="mb-6 bg-amber-50 border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-sm font-bold">2</span>
              Edit the Excel File
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-700 space-y-2">
            <p><strong>Instructions:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Open the downloaded file in Excel</li>
              <li>Update existing staff details as needed</li>
              <li>Add new staff members at the bottom of the list</li>
              <li><strong>Do NOT change the column order or headers</strong></li>
              <li>Use format YYYY-MM-DD for dates (e.g., 1990-05-15)</li>
              <li>Save the file when done</li>
            </ul>
          </CardContent>
        </Card>

        {/* Step 3: Upload */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold">3</span>
              Upload Updated File
            </CardTitle>
            <CardDescription>
              Upload your edited Excel file to update the staff directory
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="cursor-pointer">
              <div className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-all
                ${uploading ? 'border-indigo-400 bg-indigo-50' : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'}
              `}>
                {uploading ? (
                  <div className="flex items-center justify-center gap-2 text-indigo-600">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                    <span>Uploading and processing...</span>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                    <p className="text-slate-600 mb-1">Drop your updated file here</p>
                    <p className="text-sm text-indigo-600">or click to browse</p>
                  </>
                )}
              </div>
              <input
                type="file"
                className="hidden"
                accept=".xlsx,.xls"
                onChange={(e) => handleUpload(e.target.files[0])}
                disabled={uploading}
                data-testid="hr-upload-input"
              />
            </label>

            {/* Upload Results */}
            {uploadResult && (
              <div className={`rounded-lg p-4 ${uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {uploadResult.success ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-700 font-medium">
                      <CheckCircle className="h-5 w-5" />
                      Upload Successful!
                    </div>
                    <div className="text-sm text-green-600 space-y-1">
                      <p>{uploadResult.imported} new staff members added</p>
                      <p>{uploadResult.updated} existing records updated</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5" />
                    <span>{uploadResult.error || "Upload failed"}</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-slate-500">
          <p>This link expires on {new Date(linkInfo?.expires_at).toLocaleDateString()}</p>
          <p className="mt-1">Europacific Staff Directory Management</p>
        </div>
      </div>
    </div>
  );
}
